import { Injectable } from '@angular/core';
import { WebClient } from '@injectables';
import { Device, NodeConnection } from '@models';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { FlowNode } from './node-manager';
import { BehaviorSubject, tap } from 'rxjs';
import { FoblexNode } from './base-node/base-node';
import { BaseConnection } from './base-connection';


@Injectable({ providedIn: 'root' })
export class ConnectionManager {
    constructor(
        private readonly fragQuery: FragQueryService,
        private readonly webClient: WebClient,
    ) {}

    public readonly device$ = this.fragQuery.device$;
    public readonly siteId$ = this.fragQuery.siteId$;
    public readonly nodeConnectionClient = this.webClient.model.nodeConnection;
    private connections: BaseConnection<NodeConnection.ServerSideMeta>[] = [];
    private connectionsSubject = new BehaviorSubject<BaseConnection<NodeConnection.ServerSideMeta>[]>([]);
    public readonly connections$ = this.connectionsSubject.asObservable();

    public loadInitialConnections() {
        this.fragQuery.nodeConnections$.pipe(
            tap(connections => {
                this.connections = [];
                if (!connections) {
                    this.connectionsSubject.next([]);
                    return;
                }
                connections.forEach(c => this.addConnection(c.toBaseConnection()));
            })
        ).subscribe();
    }

    public async addConnectionOnConnect(from: string, to: string) {
        if (!this.connections.find(c => c.from === from && c.to === to) && from && to) {
            const connection = new NodeConnection();
            connection.siteId = this.siteId$.value;
            connection.from = from;
            connection.to = to;

            await this.nodeConnectionClient.save(connection);
            this.connections.push(connection.toBaseConnection());
            this.connectionsSubject.next([...this.connections]);
        }
    }

    public reassignConnection(from: string, to: string, newTo: string) {
        const oldConnectionIndex = this.connections.findIndex(
            (c) => c.from === from && c.to === to
        );

        if (oldConnectionIndex !== -1) {
            this.connections[oldConnectionIndex].to = newTo;
            this.nodeConnectionClient.save(NodeConnection.fromBaseConnection(this.connections[oldConnectionIndex]))
        }
    }

    private addConnection(connection: BaseConnection<NodeConnection.ServerSideMeta>): void {
        const exists = this.connections.some(n => n.id === connection.id);
        if (!exists) {
            this.connections.push(connection);
            this.connectionsSubject.next([...this.connections]);
        }
    }

    public getConnections(): BaseConnection<NodeConnection.ServerSideMeta>[] {
        return [...this.connections];
    }

    public async RemoveConnectionOnNodeRemove(node: FlowNode) {
        const connections = this.getConnections()
        if(FoblexNode.isBaseNode(node) && connections){
            const connectionsToDelete = connections.filter(conn =>
                conn.from === node.outputId || conn.to === node.inputId
            );
    
            for (const conn of connectionsToDelete) {
                await this.nodeConnectionClient.delete(NodeConnection.fromBaseConnection(conn));
                this.connections = this.connections.filter(c => c.id !== conn.id);
            }
    
            this.connections = this.connections.filter(conn =>
                !(conn.from === node.outputId || conn.to === node.inputId)
            );
    
            this.connectionsSubject.next([...this.connections]);
        }
    }

    public removeAllConnections(): void {
        this.connections = [];
        this.connectionsSubject.next([]);
    }
}
